import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { format as formatDate } from 'date-fns';
import { performanceApi } from '../../services/api';

const formatCurrency = (value) =>
  new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value || 0);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1.5 }}>
      <Typography variant="body2" fontWeight="bold" gutterBottom>
        {label}
      </Typography>
      {payload.map((entry) => (
        <Typography key={entry.dataKey} variant="body2" sx={{ color: entry.color }}>
          {entry.name}: {entry.dataKey === 'orders' ? entry.value : formatCurrency(entry.value)}
        </Typography>
      ))}
    </Paper>
  );
};

const NetworkTrendCharts = ({ fromDate, toDate, retailerIds }) => {
  const [trends, setTrends] = useState([]);
  const [granularity, setGranularity] = useState('day');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartView, setChartView] = useState('revenue');

  const fetchTrends = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await performanceApi.getTrends({
        from_date: fromDate,
        to_date: toDate,
        retailer_ids: retailerIds,
      });
      if (result.success) {
        setTrends(result.data || []);
        setGranularity(result.granularity || 'day');
      } else {
        setError(result.error || 'Failed to load trends');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, retailerIds]);

  useEffect(() => {
    fetchTrends();
  }, [fetchTrends]);

  if (loading) {
    return (
      <Paper sx={{ p: 2 }} elevation={1}>
        <Typography variant="h6" gutterBottom>Trends</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (error) return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;

  if (trends.length === 0) {
    return (
      <Paper sx={{ p: 2 }} elevation={1}>
        <Typography variant="h6" gutterBottom>Trends</Typography>
        <Typography color="text.secondary">No trend data for this period.</Typography>
      </Paper>
    );
  }

  const chartData = trends.map((t) => {
    const d = new Date(t.period);
    let label;
    if (granularity === 'day') {
      label = formatDate(d, 'MMM d');
    } else if (granularity === 'week') {
      const end = new Date(d);
      end.setDate(end.getDate() + 6);
      label = `${formatDate(d, 'd')}-${formatDate(end, 'd MMM')}`;
    } else {
      label = formatDate(d, "MMM ''yy");
    }
    return { ...t, label };
  });

  const xInterval = chartData.length > 14 ? Math.floor(chartData.length / 10) : 0;

  return (
    <Paper sx={{ p: 2 }} elevation={1}>
      <Typography variant="h6" gutterBottom>Trends</Typography>

      <ToggleButtonGroup
        value={chartView}
        exclusive
        onChange={(_, v) => v && setChartView(v)}
        size="small"
        fullWidth
        sx={{ mb: 2 }}
      >
        <ToggleButton value="revenue">Revenue</ToggleButton>
        <ToggleButton value="orders">Orders</ToggleButton>
        <ToggleButton value="commission">Commission</ToggleButton>
      </ToggleButtonGroup>

      <ResponsiveContainer width="100%" height={350}>
        {chartView === 'revenue' ? (
          <BarChart data={chartData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" tick={{ fontSize: 12 }} interval={xInterval} />
            <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => formatCurrency(v)} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar dataKey="gmv" name="GMV" fill="#1976d2" />
            <Bar dataKey="total_commission" name="Total Commission" fill="#9c27b0" />
          </BarChart>
        ) : chartView === 'orders' ? (
          <BarChart data={chartData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" tick={{ fontSize: 12 }} interval={xInterval} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar dataKey="orders" name="Orders" fill="#2e7d32" />
          </BarChart>
        ) : (
          <BarChart data={chartData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" tick={{ fontSize: 12 }} interval={xInterval} />
            <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => formatCurrency(v)} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar dataKey="handoff_commission" name="Handoff Commission" fill="#ed6c02" />
            <Bar dataKey="retailer_commission" name="Retailer Commission" fill="#2e7d32" />
          </BarChart>
        )}
      </ResponsiveContainer>
    </Paper>
  );
};

export default NetworkTrendCharts;
